import {undefinedIfEmpty} from '@kapitalist/utils/falsiness'
import {id} from '@kapitalist/utils/function'
import {recordKeySet} from '@kapitalist/utils/Record'
import {safelyOr} from '@kapitalist/utils/safely'
import * as S from '@kapitalist/utils/Set'
import {rawEnv, type TRawEnvVarName} from '~/config/environment/rawEnv'
import type {
  TBackendSource,
  TEnvironment,
  TNodeEnv,
  TRootDomainName,
} from '~/config/environment/types'

// -------
// Helpers
// -------

const getRawEnvVar = (name: TRawEnvVarName) => undefinedIfEmpty(rawEnv[name])

const optionalEnvVar = <T>(
  name: TRawEnvVarName,
  fallback: T,
  // can either throw or default if invalid
  validator: (raw: string) => T
): T => safelyOr(getRawEnvVar(name), validator, fallback)

const envVarMissingError = (name: TRawEnvVarName): never => {
  throw new Error(`Expected environment variable ${name}`)
}

const requiredEnvVarFromSet = <T>(name: TRawEnvVarName, set: Set<T>): T =>
  requiredEnvVar(name, raw => S.totalSetMemberOrThrow(raw, set, name))

const requiredEnvVar = <T>(
  name: TRawEnvVarName,
  // throw here if invalid
  validator: (raw: string) => T
): T => {
  const raw = getRawEnvVar(name) ?? envVarMissingError(name)
  return validator(raw)
}

// -------
// Parsing and validating actual values
//
// Note that any parsed-as-required values that aren't present during
// things like test runs should not be read here at the module level.
// -------

const nodeEnv = requiredEnvVarFromSet(
  'NODE_ENV',
  recordKeySet<TNodeEnv>({
    development: true,
    production: true,
    test: true,
  })
)

// -------
// Main exports
// -------

export const inTesting: boolean = nodeEnv === 'test'

/**
 * Always remember that anything stored here will be exposed via our public JS bundle.
 */
const environment: TEnvironment = inTesting
  ? {
      // we always want tests to hit the mocks.
      backendSource: 'mock',
      rootDomainName: 'kapitalist.io',
      gitSha: '1234abcdef',
      ngrokSubdomain: 'abcdefg',
      ipBackendIp: '0.0.0.0',
      nodeEnv,
    }
  : {
      backendSource: requiredEnvVarFromSet(
        'PUBLIC_BACKEND_SOURCE',
        recordKeySet<TBackendSource>({
          mock: true,
          localhost: true,
          localhost_named: true,
          ip: true,
          ngrok: true,
          ngroks: true,
          production: true,
        })
      ),
      rootDomainName: requiredEnvVarFromSet(
        'PUBLIC_DOMAIN_NAME',
        recordKeySet<TRootDomainName>({
          'kapitalist.io': true,
          'mercury.deals': true,
        })
      ),
      gitSha: requiredEnvVar('PUBLIC_GIT_SHA', id),
      ngrokSubdomain: optionalEnvVar('PUBLIC_NGROK_SUBDOMAIN', 'unset', id),
      ipBackendIp: optionalEnvVar('PUBLIC_IP_BACKEND_IP', '0.0.0.0', id),
      nodeEnv,
    }

export default environment
